import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../components/Icon';

export interface ToastInput {
  icon: IconName;
  title: string;
  sub?: ReactNode;
  /** 0..1 shows a progress bar and keeps the toast open until closed */
  progress?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

export interface ToastHandle {
  update(patch: Partial<ToastInput>): void;
  close(delay?: number): void;
}

interface ToastApi {
  toast(t: ToastInput): ToastHandle;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const remove = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);

  const toast = useCallback(
    (t: ToastInput): ToastHandle => {
      const id = ++seq.current;
      setItems((l) => [...l, { ...t, id }]);
      if (t.progress === undefined) setTimeout(() => remove(id), 4200);
      return {
        update: (patch) => setItems((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x))),
        close: (delay = 0) => setTimeout(() => remove(id), delay),
      };
    },
    [remove],
  );

  const api = useMemo(() => ({ toast }), [toast]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div className="toast" key={t.id}>
            <div className="tt">
              <Icon name={t.icon} />
              {t.title}
            </div>
            {t.sub ? <div className="ts">{t.sub}</div> : <div className="ts" />}
            {t.progress !== undefined && (
              <div className="bar">
                <i style={{ width: `${Math.round(Math.min(1, t.progress) * 100)}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi['toast'] {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c.toast;
}
